import { useIntl } from 'react-intl';
import { Button, Col, Form, Input, Row, Typography } from 'antd';
import Table, { ColumnsType } from 'antd/lib/table';
import {
  useDefaultEmptyFieldError,
  useDisclosure,
  useWindowSize,
} from '@/hooks';
import { FieldData } from '@/types/FieldData';
import { StockItem } from '@/core/domain/stock/StockItems/StockItems.types';
import FloatingFormItem from '@/components/_common/FloatingFormItem';
import {
  CompaniesInfiniteSelectField,
  DeleteIcon,
  InputDecimal,
} from '@/components/_common';
import { quantityFormatter } from '@/utils/number';
import StatusTag from '@/components/_common/StatusTag';
import { getTableScrollX } from '@/utils/table';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import StockTransferSelectItemsModal from '../StockTransferSelectItemsModal';
import { StockTransferFormValues } from '@/core/domain/stock/Operations/Operations.types';
import AntdFeatherIcon from '@/components/_common/AntdFeatherIcon';
import { ArrowDown, ArrowRight } from 'react-feather';

const { Title, Text } = Typography;

export const getRowKey = (item: StockItem) =>
  JSON.stringify({
    partId: item.partId,
    consumableId: item.consumableId,
  });

interface Props {
  isEdit?: boolean;
}

export default function StockTransferForm(props: Props) {
  const intl = useIntl();
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const { isMobile } = useWindowSize();

  const form = Form.useFormInstance<StockTransferFormValues>();

  const [stockItemsSelectOpened, stockItemsSelectActions] = useDisclosure();

  const items = Form.useWatch('items', form);

  const companySendStockId = Form.useWatch('companySendStockId', form);

  const handleFieldsChange = (changedFields: FieldData[]) => {
    const [field] = changedFields;

    if (field && (field.name as string[])[0] === 'companySendStockId') {
      form.setFieldValue('items', []);
    }
  };

  const columnPart = useConfigurationColumnsPart({ internalCode: 'sku' });

  const columns: ColumnsType<StockItem> = [
    columnPart,
    {
      ellipsis: true,
      dataIndex: 'description',
      title: 'Descrição',
    },
    {
      width: 120,
      title: 'Tipo',
      render: (row: StockItem) => (
        <StatusTag label={row.partId ? 'Peça' : 'Insumo'} color="blue" />
      ),
    },
    !props.isEdit
      ? {
          width: 140,
          dataIndex: 'stock',
          title: 'Estoque disponível',
          render: (_, record: StockItem) => {
            if (!companySendStockId) return '-';

            const companyStock = record.stock?.find(
              (a) => companySendStockId === a.company.id,
            );

            if (!companyStock) return '-';

            return quantityFormatter(companyStock?.availableStock);
          },
        }
      : {
          width: 140,
          dataIndex: 'oldQuantity',
          title: 'Quantidade anterior',
          render: (value) => quantityFormatter(value),
        },
    !props.isEdit
      ? {
          width: 185,
          title: 'Quantidade',
          render(row: StockItem, record, index) {
            if (!companySendStockId) return '-';

            const companyStock = record.stock?.find(
              (a) => companySendStockId === a.company.id,
            );

            if (!companyStock) return '-';

            return (
              <>
                {row.partId && (
                  <Form.Item
                    hidden
                    name={[index, 'partId']}
                    initialValue={row.partId}
                  >
                    <InputDecimal fractions={2} min={0} />
                  </Form.Item>
                )}

                {row.consumableId && (
                  <Form.Item
                    hidden
                    name={[index, 'consumableId']}
                    initialValue={row.consumableId}
                  >
                    <InputDecimal fractions={2} min={0} />
                  </Form.Item>
                )}

                <Form.Item
                  style={{ margin: 'auto' }}
                  name={[index, 'quantity']}
                  initialValue={0}
                  rules={[
                    {
                      type: 'number',
                      max: companyStock.availableStock,
                      message: `Quantidade máxima: ${companyStock.availableStock}`,
                    },
                  ]}
                >
                  <InputDecimal fractions={2} min={0} disabled={props.isEdit} />
                </Form.Item>
              </>
            );
          },
        }
      : {
          width: 185,
          title: 'Quantidade transferida',
          dataIndex: 'quantity',
          render: (value) => quantityFormatter(value),
        },
    {
      width: 60,
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      render: (row) => (
        <DeleteIcon
          onClick={() => {
            const rowKey = getRowKey(row);

            const itemsStock = items.filter((si) => {
              const siRowKey = getRowKey(si);

              return rowKey !== siRowKey;
            });

            form.setFieldValue('items', itemsStock);
          }}
          disabled={props.isEdit}
        />
      ),
      fixed: 'right',
      align: 'center',
    },
  ];

  return (
    <Form form={form} onFieldsChange={handleFieldsChange} preserve={false}>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Form.Item
          noStyle
          hidden
          name="items"
          shouldUpdate
          initialValue={[]}
          rules={[
            {
              type: 'array',
              min: 1,
              message: intl.formatMessage({
                id: 'stockCorrections.create.errors.atLeastOneItem',
              }),
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Col span={24} md={8}>
          <FloatingFormItem
            name="companySendStockId"
            label="Empresa de origem"
            rules={[defaultEmptyFieldError]}
          >
            <CompaniesInfiniteSelectField disabled={props.isEdit} />
          </FloatingFormItem>
        </Col>

        <Col
          span={24}
          md={8}
          style={{ display: 'flex', justifyContent: 'center' }}
        >
          {isMobile ? (
            <AntdFeatherIcon icon={<ArrowDown />} size={32} />
          ) : (
            <AntdFeatherIcon icon={<ArrowRight />} size={32} />
          )}
        </Col>

        <Col span={24} md={8}>
          <FloatingFormItem
            name="companyReceiveStockId"
            label="Empresa de destino"
            rules={[
              defaultEmptyFieldError,
              (ctx) => ({
                type: 'number',
                validator(_, value) {
                  if (ctx.getFieldValue('companySendStockId') === value) {
                    return Promise.reject(
                      new Error(
                        'Empresa de destino deve ser diferente da empresa de origem',
                      ),
                    );
                  }

                  return Promise.resolve();
                },
              }),
            ]}
          >
            <CompaniesInfiniteSelectField disabled={props.isEdit} />
          </FloatingFormItem>
        </Col>
      </Row>

      <Row align="middle" justify="center" gutter={[16, 16]}>
        <Col flex={1}>
          <Title level={3} style={{ margin: 0 }}>
            {props.isEdit ? 'Itens transferidos' : 'Itens a transferir'}
          </Title>
        </Col>

        {!props.isEdit && (
          <Col>
            <Button
              block
              type="primary"
              disabled={!companySendStockId}
              onClick={stockItemsSelectActions.open}
            >
              Adicionar
            </Button>
          </Col>
        )}

        <Col span={24}>
          <Form.Item noStyle shouldUpdate>
            {() => (
              <div style={{ marginBottom: '16px' }}>
                <Text type="danger">{form.getFieldError(['items'])}</Text>
              </div>
            )}
          </Form.Item>

          <Form.List name="items">
            {() => (
              <Table
                size="middle"
                columns={columns}
                dataSource={form.getFieldValue('items')}
                scroll={{ x: getTableScrollX(columns) }}
                rowKey={(row) => getRowKey(row)}
                pagination={false}
              />
            )}
          </Form.List>
        </Col>
      </Row>

      <StockTransferSelectItemsModal
        selectedItems={items}
        companyId={companySendStockId}
        open={stockItemsSelectOpened}
        onCancel={stockItemsSelectActions.close}
      />
    </Form>
  );
}
